import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import './ATS_SyncEnv.scss';
import { _keys, filterKeys, RuntimeModules, tsValidateResult, tsValidateUniqueId } from '@nu-art/ts-common';
import { ATS_Fullstack, TS_AppTools } from '../../components/TS_AppTools/index.js';
import { genericNotificationAction } from '../../components/TS_Notifications/index.js';
import { ModuleFE_SyncEnvV2 } from '../../modules/sync-env/ModuleFE_SyncEnvV2.js';
import { LL_H_C, LL_V_L } from '../../components/Layouts/index.js';
import { TS_Checkbox } from '../../components/TS_Checkbox/index.js';
import { TS_Input } from '../../components/TS_Input/index.js';
import { ComponentSync } from '../../core/ComponentSync.js';
import { Thunder } from '../../core/Thunder.js';
import { TS_PropRenderer } from '../../components/TS_PropRenderer/index.js';
import { SimpleListAdapter } from '../../components/adapter/Adapter.js';
import { TS_DropDown } from '../../components/TS_Dropdown/index.js';
import { _className } from '../../utils/tools.js';
import { StorageKey } from '../../modules/ModuleFE_LocalStorage.js';
import { ModuleFE_SyncManager } from '../../modules/sync-manager/ModuleFE_SyncManager.js';
import { Button } from '../../components/Button/Button.js';
const Environments = ['prod', 'staging', 'dev', 'local'];
const ChunkSizes = [2000, 1000, 500, 200, 100, 50];
const StorageKey_BackupId = new StorageKey('sync-env--backup-id');
const StorageKey_Env = new StorageKey('sync-env--env');
const StorageKey_ChunkSize = new StorageKey('sync-env--chunk-size');
export class ATS_SyncEnvironment extends ComponentSync {
    static screen = {
        name: 'Sync Environment',
        key: 'sync-environment',
        renderer: this,
        group: ATS_Fullstack
    };
    // ######################## Life Cycle ########################
    deriveStateFromProps(nextProps, state) {
        //Set Initials
        state.selectedModules ??= new Set();
        state.selectedChunkSize ??= StorageKey_ChunkSize.get(100);
        state.selectedEnv ??= StorageKey_Env.get('prod');
        state.backupId ??= StorageKey_BackupId.get();
        state.cleanSync ??= false;
        state.delta ??= false;
        state.deleteMissing ??= false;
        state.forceFull ??= false;
        state.fullBackup ??= false;
        //Add local module data
        state.moduleList = this.getCollectionModuleList().reduce((toRet, name) => {
            toRet[name] = { local: true };
            return toRet;
        }, {});
        if (state.metadata) {
            state.metadata.collectionsData.forEach(moduleData => {
                state.moduleList[moduleData.dbKey] = { ...state.moduleList[moduleData.dbKey], backup: true };
            });
            state.metadata.remoteCollectionNames.forEach(moduleName => {
                state.moduleList[moduleName] = { ...state.moduleList[moduleName], remote: true };
            });
        }
        return state;
    }
    // ######################## Logic ########################
    fetchMetadata = async () => {
        if (!this.state.backupId?.length)
            return;
        if (!this.state.selectedEnv)
            return;
        this.setState({ fetchMetadataInProgress: true });
        try {
            await genericNotificationAction(async () => {
                const metadata = await ModuleFE_SyncEnvV2.vv1.fetchBackupMetadata({
                    env: this.state.selectedEnv,
                    backupId: this.state.backupId,
                }).executeSync();
                this.reDeriveState({ metadata: metadata });
            }, 'Fetching backup metadata');
        }
        catch (err) {
            this.logError(err);
        }
        this.setState({ fetchMetadataInProgress: false });
    };
    syncEnv = async () => {
        if (!this.canSync())
            return;
        const start = performance.now();
        ModuleFE_SyncManager.stopListening();
        await genericNotificationAction(async () => {
            try {
                await ModuleFE_SyncEnvV2.vv1.syncFromEnvBackup(filterKeys({
                    env: this.state.selectedEnv,
                    backupId: this.state.backupId,
                    chunkSize: this.state.selectedChunkSize,
                    selectedModules: Array.from(this.state.selectedModules),
                    cleanSync: this.state.cleanSync,
                    delta: this.state.delta,
                    deleteMissing: this.state.delta && this.state.deleteMissing,
                    forceFull: this.state.delta && this.state.forceFull,
                }, 'selectedModules')).executeSync();
            }
            catch (err) {
                this.logError(err);
            }
            finally {
                ModuleFE_SyncManager.startListening();
            }
        }, 'Syncing Env');
        const end = performance.now();
        this.setState({ restoreTime: `${((end - start) / 1000).toFixed(3)} seconds` });
    };
    syncFirebase = async () => {
        if (!this.canSync())
            return;
        await genericNotificationAction(async () => {
            await ModuleFE_SyncEnvV2.vv1.syncFirebaseFromBackup({
                env: this.state.selectedEnv,
                backupId: this.state.backupId
            }).executeSync();
        }, 'Syncing Firebase');
    };
    createNewBackup = async () => {
        return genericNotificationAction(async () => {
            this.setState({ backingUpInProgress: true }, async () => {
                const toRet = await ModuleFE_SyncEnvV2.vv1.createBackup({ delta: this.state.fullBackup ? 'false' : 'true' }).executeSync();
                this.setState({ backingUpInProgress: false });
                return toRet;
            });
        }, 'Create Backup');
    };
    canSync = () => {
        return !!this.state.selectedEnv && !!this.state.backupId;
    };
    getCollectionModuleList() {
        //the moduleKey in ModuleBE_BaseDB's config is taken from collection's name.
        return (RuntimeModules().filter((module) => !!module.dbDef?.dbKey)).map(_module => _module.dbDef.dbKey).sort();
    }
    allModulesSelected = () => {
        return this.state.selectedModules.size === _keys(this.state.moduleList).length;
    };
    // ######################## Set Data
    setBackupId = (id) => {
        this.setState({ backupId: id }, () => {
            if (!id)
                return StorageKey_BackupId.delete();
            StorageKey_BackupId.set(id);
            if (tsValidateResult(this.state.backupId, tsValidateUniqueId)) //Don't fetch metadata if id doesn't fit
                return;
            return this.fetchMetadata();
        });
    };
    setEnv = (env) => {
        this.setState({ selectedEnv: env }, () => {
            if (!env)
                return StorageKey_Env.delete();
            StorageKey_Env.set(env);
            this.fetchMetadata();
        });
    };
    setChunkSize = (size) => {
        this.setState({ selectedChunkSize: size });
        if (size)
            return this.fetchMetadata();
    };
    toggleSelectAll = () => {
        const selectedLength = this.state.selectedModules.size;
        const allModules = _keys(this.state.moduleList);
        //All are selected, clear all modules
        if (selectedLength === allModules.length)
            return this.setState({ selectedModules: new Set() });
        //Some or none of the modules are selected, select all
        this.setState({ selectedModules: new Set(allModules) });
    };
    toggleSelectedModule = (moduleName) => {
        if (this.state.selectedModules.has(moduleName))
            this.state.selectedModules.delete(moduleName);
        else
            this.state.selectedModules.add(moduleName);
        this.forceUpdate();
    };
    // ######################## Render ########################
    render() {
        return _jsxs(LL_V_L, { className: 'sync-env-page', children: [this.renderHeader(), this.renderMenu(), this.renderBackupModules()] });
    }
    renderHeader = () => {
        return TS_AppTools.renderPageHeader('Sync Environment V2');
    };
    renderMenu = () => {
        const envAdapter = SimpleListAdapter([...Environments], item => _jsx("div", { className: 'node-data', children: item.item }));
        const chunkSizesAdapter = SimpleListAdapter([...ChunkSizes], item => _jsx("div", { className: 'node-data', children: item.item }));
        return _jsxs(LL_V_L, { className: 'sync-env-page__menu', children: [_jsxs(LL_H_C, { className: 'sync-env-page__menu__row', children: [_jsx(TS_PropRenderer.Vertical, { label: 'Environment', children: _jsx(TS_DropDown, { placeholder: 'Select Environment', className: 'fancy', adapter: envAdapter, onSelected: this.setEnv, selected: this.state.selectedEnv, canUnselect: true }) }), _jsx(TS_PropRenderer.Vertical, { label: 'Chunk Size', children: _jsx(TS_DropDown, { placeholder: 'Chunk Size', className: 'fancy', adapter: chunkSizesAdapter, onSelected: this.setChunkSize, selected: this.state.selectedChunkSize, canUnselect: true }) }), this.renderOperations()] }), _jsxs(LL_H_C, { className: 'sync-env-page__menu__row', children: [_jsx(TS_PropRenderer.Vertical, { label: 'Backup ID', children: _jsx(TS_Input, { className: _className(!tsValidateResult(this.state.backupId, tsValidateUniqueId) ? 'valid-id' : 'invalid-id'), type: 'text', value: this.state.backupId, onBlur: this.setBackupId }) }), _jsx(TS_PropRenderer.Vertical, { label: 'Collection Filter', children: _jsx(TS_Input, { onChange: val => this.setState({ searchFilter: val }), type: 'text', placeholder: 'search collection' }) }), this.renderStatus(), this.state.restoreTime && _jsx("div", { children: this.state.restoreTime })] })] });
    };
    renderOperations = () => {
        return _jsx(TS_PropRenderer.Vertical, { label: 'Operations', children: _jsxs(LL_H_C, { className: 'sync-env-page__operations', children: [_jsx(Button, { onClick: this.createNewBackup, children: "Trigger Backup" }), _jsx(TS_Checkbox, { checked: this.state.fullBackup, onCheck: (value) => {
                            this.setState({ fullBackup: value });
                        }, children: "Full Backup" }), _jsx(Button, { onClick: this.syncEnv, disabled: !this.canSync(), children: "Sync Environment" }), Thunder.getInstance().getConfig().name?.toLowerCase() === this.state.selectedEnv && _jsx(Button, { onClick: this.syncFirebase, disabled: !this.canSync(), className: 'deter-users-from-this-button', children: "Restore Firebase To Older Backup" }), _jsx(TS_Checkbox, { checked: this.state.cleanSync, onCheck: (value) => {
                            this.setState({ cleanSync: value });
                        }, children: "Clean Sync" }), _jsx(TS_Checkbox, { checked: this.state.delta, onCheck: (value) => {
                            this.setState({ delta: value });
                        }, children: "Delta (change-tracked)" }), _jsx(TS_Checkbox, { disabled: !this.state.delta, checked: this.state.delta && this.state.deleteMissing, onCheck: (value) => {
                            this.setState({ deleteMissing: value });
                        }, children: "Delete Missing" }), _jsx(TS_Checkbox, { disabled: !this.state.delta, checked: this.state.delta && this.state.forceFull, onCheck: (value) => {
                            this.setState({ forceFull: value });
                        }, children: "Force Full" })] }) });
    };
    renderStatus = () => {
        const style = {
            '--color': '#000',
            '--color-faded': '#0008',
        };
        if (this.state.fetchMetadataInProgress)
            return _jsxs(LL_H_C, { className: 'sync-env-page__status', children: ["Fetching Metadata", _jsxs("span", { style: style, className: 'three-dot-loader', children: [_jsx("span", {}), _jsx("span", {}), _jsx("span", {})] })] });
        if (this.state.backingUpInProgress)
            return _jsxs(LL_H_C, { className: 'sync-env-page__status', children: ["Backup In Progress", _jsxs("span", { style: style, className: 'three-dot-loader', children: [_jsx("span", {}), _jsx("span", {}), _jsx("span", {})] })] });
    };
    renderBackupModules = () => {
        if (!this.canSync())
            return;
        return _jsx(_Fragment, { children: _jsxs(LL_V_L, { className: 'sync-env_modules-list-v2', children: [_jsx(LL_H_C, { className: 'utils', children: _jsx(TS_Checkbox, { checked: this.allModulesSelected(), onCheck: this.toggleSelectAll, children: "Select All" }) }), _keys(this.state.moduleList).map(this.renderModule)] }) });
    };
    renderModule = (moduleName) => {
        const collectionMetadata = this.state.metadata?.collectionsData.find(collection => collection.dbKey === moduleName);
        if ((this.state.searchFilter && this.state.searchFilter.length) && !moduleName.includes(this.state.searchFilter))
            return;
        const relevantLocalModules = RuntimeModules().filter((module) => {
            return (!!module.getCollectionName && module.getCollectionName() == collectionMetadata?.dbKey);
        });
        const localCount = relevantLocalModules.length === 1 && relevantLocalModules[0] ? relevantLocalModules[0].cache._array.length : 0;
        const diffCount = collectionMetadata?.numOfDocs !== undefined ? collectionMetadata.numOfDocs - localCount : undefined;
        const diffShow = diffCount !== 0 ? diffCount : undefined;
        return _jsx(TS_PropRenderer.Horizontal, { label: _jsxs(LL_H_C, { className: 'header', children: [_jsx(TS_Checkbox, { checked: this.state.selectedModules.has(moduleName), onCheck: () => this.toggleSelectedModule(moduleName) }), _jsx("div", { children: moduleName })] }), children: _jsxs(LL_H_C, { className: 'collection-row', children: [_jsxs(LL_H_C, { className: 'backup-info', children: [diffShow !== undefined &&
                                _jsx("div", { className: _className(diffShow > 0 ? 'higher' : 'lower'), children: `${diffShow > 0 ? '+' : ''}${diffShow}` }), _jsx("div", { children: collectionMetadata?.numOfDocs !== undefined ? collectionMetadata?.numOfDocs : '--' }), "|", _jsx("div", { className: 'left-row', children: collectionMetadata?.version || '--' })] }), _jsx("div", { style: { fontFamily: 'monospace' }, children: `${this.state.moduleList[moduleName].local ? 'L' : '-'}${this.state.moduleList[moduleName].remote
                            ? 'R'
                            : '-'}${this.state.moduleList[moduleName].backup ? 'B' : '-'}` })] }) }, moduleName);
    };
}
//# sourceMappingURL=ATS_SyncEnv.js.map